// OPTIMIZED CategoryToolsPage.tsx - Improved SEO & AdSense Performance
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toolCategories } from '../data/toolsByCategory';
import ToolCard from '../components/Home/ToolCard';
import slugify from '../utils/slugify';
import useIsMobile from '../hooks/useIsMobile';
import { Category } from '../data/types';
import InFeedAd from '../components/Ads/InFeedAd';
import { Helmet } from 'react-helmet-async';

interface CategoryToolsPageProps {
  initialSlug?: string;
}

const CategoryToolsPage: React.FC<CategoryToolsPageProps> = ({ initialSlug }) => {
  const { category } = useParams();
  const slug = initialSlug || category || '';
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [categorySearch, setCategorySearch] = useState('');
  const [toolSearch, setToolSearch] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [priceFilter, setPriceFilter] = useState('all');

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const toolSearchText = toolSearch.trim().toLowerCase();
  const categorySearchText = categorySearch.trim().toLowerCase();

  const isSelfHostedPage = slug === 'self-hosted-ai-tools';

  const isSelfHostedTool = useMemo(() => (tool: any) => {
    const selfHostedIndicators = [
      'self-hosted', 'self hosted', 'on-premise', 'on-premises', 
      'local deployment', 'private deployment', 'docker', 
      'kubernetes', 'helm', 'opensource', 'open source', 'selfhosted',
      'github', 'self deploy', 'private cloud'
    ];

    const searchableText = [
      tool.title,
      tool.description,
      tool.longDescription,
      ...(tool.tags || [])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return selfHostedIndicators.some(indicator => 
      searchableText.includes(indicator)
    );
  }, []);

  const currentCategory = isSelfHostedPage 
    ? { title: 'Self-Hosted AI Tools', tools: [] }
    : (slug ? toolCategories.find(cat => slugify(cat.title) === slug) : null);

  const filteredCategories = useMemo(() => {
    return toolCategories
      .map(cat => {
        const matchesSlug = !slug || slugify(cat.title) === slug || isSelfHostedPage;

        let filteredTools = cat.tools.filter(tool => {
          if (isSelfHostedPage && !isSelfHostedTool(tool)) {
            return false;
          }

          const searchable = [tool.title, tool.description, tool.longDescription]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          const matchesSearch = !toolSearchText || searchable.includes(toolSearchText);
          const matchesPrice = priceFilter === 'all' || 
                             (priceFilter === 'free' && tool.isFree) ||
                             (priceFilter === 'paid' && !tool.isFree);

          return matchesSearch && matchesPrice;
        });

        if (sortBy === 'name') {
          filteredTools.sort((a, b) => a.title.localeCompare(b.title));
        } else if (sortBy === 'free') {
          filteredTools.sort((a, b) => (b.isFree ? 1 : 0) - (a.isFree ? 1 : 0));
        }

        return matchesSlug && filteredTools.length > 0
          ? { ...cat, tools: filteredTools }
          : null;
      })
      .filter((cat): cat is Category => cat !== null);
  }, [slug, isSelfHostedPage, toolSearchText, priceFilter, sortBy, isSelfHostedTool]);

  const sidebarCategories = useMemo(() => 
    toolCategories.filter(cat =>
      cat.title.toLowerCase().includes(categorySearchText)
    ),
    [categorySearchText]
  );

  const totalTools = useMemo(() => 
    filteredCategories.reduce((sum, cat) => sum + cat.tools.length, 0),
    [filteredCategories]
  );

  const freeToolsInCategory = useMemo(() =>
    filteredCategories.flatMap(cat => cat.tools).filter(t => t.isFree).length,
    [filteredCategories]
  );

  const selfHostedToolsCount = useMemo(() => 
    toolCategories.reduce((sum, cat) => 
      sum + cat.tools.filter(tool => isSelfHostedTool(tool)).length, 0
    ),
    [isSelfHostedTool]
  );

  // IMPROVED SEO DATA
  const seoData = useMemo(() => {
    const priceContext = priceFilter === 'free' ? 'Free ' : priceFilter === 'paid' ? 'Premium ' : '';
    const searchContext = toolSearchText ? ` for ${toolSearchText}` : '';

    if (isSelfHostedPage) {
      return {
        title: `${selfHostedToolsCount}+ Self-Hosted AI Tools - Private Deployment Solutions`,
        description: `Deploy ${selfHostedToolsCount}+ self-hosted AI tools on your infrastructure. Maximum privacy, control, and data ownership for enterprises and developers.`,
        keywords: 'self-hosted AI tools, private AI deployment, on-premise AI, open source AI',
        canonical: 'https://tooliq.ai/categories/self-hosted-ai-tools'
      };
    }

    if (currentCategory) {
      const categoryName = currentCategory.title;
      const toolCount = currentCategory.tools.length;
      
      return {
        title: `${priceContext}${categoryName} AI Tools - ${toolCount}+ Solutions${searchContext}`,
        description: `Find ${toolCount}+ ${priceContext.toLowerCase()}${categoryName.toLowerCase()} AI tools. Compare features, pricing, and reviews. ${freeToolsInCategory}+ free options available.`,
        keywords: `${categoryName} AI tools, ${categoryName} software, best ${categoryName} apps, ${priceFilter} ${categoryName} tools`,
        canonical: `https://tooliq.ai/categories/${slug}`
      };
    }

    return {
      title: 'AI Tools by Category - Browse Specialized Solutions',
      description: 'Explore AI tools organized by category. Find specialized solutions for your specific needs.',
      keywords: 'AI tools categories, AI software directory',
      canonical: 'https://tooliq.ai/categories'
    };
  }, [isSelfHostedPage, currentCategory, slug, selfHostedToolsCount, priceFilter, toolSearchText, freeToolsInCategory]);

  const isCategoryActive = (categoryTitle: string) => {
    if (isSelfHostedPage) return false;
    const categorySlug = slugify(categoryTitle);
    return slug === categorySlug;
  };

  const handleClearFilters = () => {
    setToolSearch('');
    setPriceFilter('all');
    setSortBy('featured');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": seoData.title,
            "description": seoData.description,
            "url": seoData.canonical,
            ...(currentCategory && {
              "about": {
                "@type": "Thing",
                "name": currentCategory.title
              },
              "mainEntity": {
                "@type": "ItemList",
                "numberOfItems": totalTools,
                "itemListElement": filteredCategories.flatMap(cat => cat.tools).slice(0, 10).map((tool, index) => ({
                  "@type": "ListItem",
                  "position": index + 1,
                  "item": {
                    "@type": "SoftwareApplication",
                    "name": tool.title,
                    "description": tool.description,
                    "applicationCategory": currentCategory.title
                  }
                }))
              }
            })
          })}
        </script>
      </Helmet>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/categories')}
                className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors group"
              >
                <svg className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">All Categories</span>
              </button>
              {(currentCategory || isSelfHostedPage) && (
                <>
                  <span className="text-gray-300">/</span>
                  <span className="text-purple-600 font-semibold">
                    {isSelfHostedPage ? 'Self-Hosted AI Tools' : currentCategory?.title}
                  </span>
                </>
              )}
            </div>

            {isMobile && (
              <button
                onClick={() => setSidebarOpen(prev => !prev)}
                className="p-2 rounded-lg bg-purple-600 text-white shadow-lg hover:bg-purple-700 transition-colors"
              >
                {sidebarOpen ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          {sidebarOpen && (
            <div className={`${isMobile ? 'w-full' : 'w-80 flex-shrink-0'} space-y-6`}>
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Categories</h2>
                  <span className="bg-purple-100 text-purple-700 text-sm px-2 py-1 rounded-full font-medium">
                    {toolCategories.length + 1}
                  </span>
                </div>

                <div className="relative mb-4">
                  <input
                    type="text"
                    value={categorySearch}
                    onChange={e => setCategorySearch(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-10 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50/50"
                    placeholder="Search categories..."
                  />
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  <button
                    onClick={() => navigate('/categories')}
                    className={`w-full text-left p-3 rounded-xl transition-all ${
                      !slug && !isSelfHostedPage
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">All Categories</span>
                      <span className={`text-sm ${!slug && !isSelfHostedPage ? 'text-white/90' : 'text-gray-500'}`}>
                        {toolCategories.reduce((sum, cat) => sum + cat.tools.length, 0)}
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate('/categories/self-hosted-ai-tools')}
                    className={`w-full text-left p-3 rounded-xl transition-all ${
                      isSelfHostedPage
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Self-Hosted AI Tools</span>
                        <span className="text-xs bg-white/20 text-white/90 px-1.5 py-0.5 rounded">
                          🏠
                        </span>
                      </div>
                      <span className={`text-sm ${isSelfHostedPage ? 'text-white/90' : 'text-gray-500'}`}>
                        {selfHostedToolsCount}
                      </span>
                    </div>
                  </button>

                  {sidebarCategories.map(cat => {
                    const isActive = isCategoryActive(cat.title);
                    const categorySlug = slugify(cat.title);

                    return (
                      <button
                        key={cat.title}
                        onClick={() => navigate(`/categories/${categorySlug}`)}
                        className={`w-full text-left p-3 rounded-xl transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium truncate">{cat.title}</span>
                          <span className={`text-sm ${isActive ? 'text-white/90' : 'text-gray-500'}`}>
                            {cat.tools.length}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl p-6 text-white shadow-lg">
                <h3 className="font-bold text-white/90 mb-4">Category Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Total Tools</span>
                    <span className="font-bold">{totalTools}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Free Options</span>
                    <span className="font-bold">{freeToolsInCategory}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Categories</span>
                    <span className="font-bold">{toolCategories.length}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6 mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {isSelfHostedPage ? 'Self-Hosted AI Tools' : (currentCategory ? currentCategory.title : 'All AI Tools')}
              </h1>
              
              {/* ADDED: SEO-RICH DESCRIPTION */}
              <div className="prose max-w-none mb-6">
                <p className="text-gray-700 text-lg">
                  {isSelfHostedPage ? (
                    <>Deploy <strong>{selfHostedToolsCount} self-hosted AI tools</strong> on your own infrastructure for maximum privacy and control. 
                    Perfect for enterprises requiring full data ownership and custom deployments.</>
                  ) : currentCategory ? (
                    <>Discover <strong>{totalTools} {currentCategory.title.toLowerCase()} AI tools</strong> with detailed comparisons, 
                    pricing info, and user reviews. {freeToolsInCategory > 0 && `${freeToolsInCategory}+ free options available.`}</>
                  ) : (
                    <>Browse AI tools across all categories. Compare features, pricing, and find the perfect solution for your needs.</>
                  )}
                </p>
              </div>

              {isSelfHostedPage && (
                <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      🏠
                    </div>
                    <div>
                      <h3 className="font-semibold text-green-900 mb-1">Why Self-Hosted?</h3>
                      <p className="text-green-800 text-sm">
                        Deploy on your infrastructure for complete control, enhanced security, and compliance with data regulations.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={toolSearch}
                      onChange={e => setToolSearch(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-12 text-base focus:ring-2 focus:ring-purple-500 bg-gray-50/50"
                      placeholder={isSelfHostedPage ? "Search self-hosted tools..." : "Search tools..."}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-xl px-3 py-3 text-sm focus:ring-2 focus:ring-purple-500 bg-white">
                    <option value="featured">Featured</option>
                    <option value="name">Name A-Z</option>
                    <option value="free">Free First</option>
                  </select>

                  <select value={priceFilter} onChange={e => setPriceFilter(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-xl px-3 py-3 text-sm focus:ring-2 focus:ring-purple-500 bg-white">
                    <option value="all">All Pricing</option>
                    <option value="free">Free Only</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {totalTools} {totalTools === 1 ? 'Tool' : 'Tools'} Found
              </h2>
            </div>

            {/* STRATEGIC AD - Only ONE after first 6 tools */}
            <div className="space-y-8">
              {filteredCategories.map((cat, idx) => (
                <section key={idx} className="space-y-6">
                  {(!slug || isSelfHostedPage) && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6">
                      <h2 className="text-2xl font-bold text-gray-900">{cat.title}</h2>
                      <p className="text-gray-600 mt-1">
                        {cat.tools.length} {cat.tools.length === 1 ? 'tool' : 'tools'} available
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {cat.tools.map((tool, index) => (
                      <React.Fragment key={index}>
                        <ToolCard
                          {...tool}
                          icon={tool.icon ?? '/default-icon.png'}
                          category={cat.title}
                        />

                        {/* SINGLE AD - Only after 12th tool */}
                        {index === 11 && (
                          <div className="col-span-1 md:col-span-2 xl:col-span-3 my-4">
                            <InFeedAd />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </section>
              ))}

              {filteredCategories.length === 0 && (
                <div className="text-center py-16 bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No tools found</h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-8">
                    {toolSearch 
                      ? `No tools matching "${toolSearch}". Try different keywords.`
                      : 'No tools match your filters.'
                    }
                  </p>
                  <button onClick={handleClearFilters}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 font-semibold">
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryToolsPage;
